import React, { useContext, useState } from "react";
import {
  IonAlert,
  IonButton,
  IonHeader,
  IonContent,
  IonToolbar,
  IonTitle,
  IonInput,
  IonLoading,
  IonCard,
  IonCardTitle,
  IonCardHeader,
  IonCardContent,
  IonIcon,
  IonButtons,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import CodeVerficationPage from "./CodeVerificationPage";
import BackendFactory from "../../API/BackendFactory";
import { onIonPressEnter } from "../../Utils";
import GlobalContext from "../../GlobalContext";
import MyFlavoriaLogo from "../../assets/my_flavoria_logo_white.png";
import { closeOutline, settingsOutline } from "ionicons/icons";
import SettingsDialog from "../SettingsDialog";
import { getGoogleLoginUrl } from "../../utils/googleSSO";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 8, flexShrink: 0 }}>
      <path fill="#4285F4" d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5831-5.0359-3.7105H-.9555v2.3317C1.4382 15.9832 4.959 18 9 18z" />
      <path fill="#FBBC05" d="M3.9641 10.7099c-.18-.54-.2822-1.1168-.2822-1.7099s.1023-1.1699.2822-1.7099V4.9583H-.9555C-1.5126 6.5899-1.8182 8.2882-1.8182 10s.3056 3.4101.8627 5.0417l3.8796-2.3318z" />
      <path fill="#EA4335" d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 4.959 0 1.4382 2.0168-.9555 4.9583l3.8796 2.3318C4.6718 5.1627 6.656 3.5795 9 3.5795z" />
    </svg>
  );
}

function LoginPage({ onDismiss }: { onDismiss?: () => void }) {
  const { t } = useTranslation();
  const [isTouched, setIsTouched] = useState(false);
  const [isValid, setIsValid] = useState<boolean>();
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const nav = document.querySelector("ion-nav");
  const backend = BackendFactory.getBackend();
  const context = useContext(GlobalContext);
  const [isOpen, setIsOpen] = useState(false);
  const [showError, setShowError] = useState(false);

  const validateEmail = (email: string) => {
    return email.match(
      /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    );
  };

  const validate = (ev: Event) => {
    const value = (ev.target as HTMLInputElement).value;

    setIsValid(undefined);

    if (value === "") return;

    const valid = validateEmail(value) !== null;

    if (valid) localStorage.setItem("email", value);

    valid ? setIsValid(true) : setIsValid(false);
  };

  const markTouched = () => {
    setIsTouched(true);
  };

  function confirmCurrent() {
    const email = localStorage.getItem("email");
    if (nav && email && isValid) {
      setShowLoading(true);
      backend.sendCodeToEmail(email).then(() => {
        setShowLoading(false);
        nav.push(() => <CodeVerficationPage />);
      }).catch(() => {
        setShowLoading(false);
        setShowError(true);
      });
    }
  }

  function logoutReason(message: string) {
    return (
      <IonCard color="primary">
        <IonCardHeader>
          <IonCardTitle>{t("You were logged out automatically!")}</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>{message}</IonCardContent>
      </IonCard>
    );
  }

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("Login / Register")}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent
        class="ion-padding"
        style={{ maxWidth: 576, alignSelf: "center" }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <img
              style={{ userSelect: "none", maxHeight: 210 }}
              draggable="false"
              className="loginMyFlavoriaLogo"
              src={MyFlavoriaLogo}
            />
          </div>
          {context.logoutReason === ""
            ? ""
            : logoutReason(context.logoutReason)}

          <p>{t("Welcome to My Flavoria app! Please register or log in by typing in your email and requesting a verification code.")}</p>
          <IonInput
            onKeyUp={(e) => {
              onIonPressEnter(e, confirmCurrent);
            }}
            className={`${isValid && "ion-valid"} ${
              isValid === false && "ion-invalid"
            } ${isTouched && "ion-touched"}`}
            fill="solid"
            color="dark"
            onIonInput={(event) => validate(event)}
            onIonBlur={() => markTouched()}
            errorText={t("Invalid E-Mail Address")}
            type="email"
            label={t("E-Mail Address")}
            placeholder={t("Type your e-mail address here")}
            labelPlacement="floating"
          ></IonInput>
          <IonButton
            onClick={confirmCurrent}
            disabled={!isValid}
            expand="block"
            size="large"
          >
            {t("Send Verification Code")}
          </IonButton>
          <br />
          <IonButton
            onClick={() => {
              window.location.href = getGoogleLoginUrl();
            }}
            expand="block"
            size="large"
            style={{
              "--background": "#ffffff",
              "--background-hover": "#f7f9fb",
              "--background-activated": "#eef1f4",
              "--color": "#1f1f1f",
              "--border-radius": "999px",
              "--border-width": "1px",
              "--border-style": "solid",
              "--border-color": "#dadce0",
              "--box-shadow": "0 1px 2px 0 rgba(60, 64, 67, 0.3)",
              "--padding-top": "14px",
              "--padding-bottom": "14px",
            }}
          >
            <GoogleIcon />
            {t("Sign in with Google")}
          </IonButton>
          <br />
          <IonButton fill="clear" onClick={() => setIsOpen(true)}>
            <IonIcon slot="start" icon={settingsOutline} />
            {t("Settings")}
          </IonButton>
          <SettingsDialog
            hideClose={true}
            applyImmediately={true}
            isOpen={isOpen}
            onDidDismiss={() => setIsOpen(false)}
          />
          <IonLoading
            isOpen={showLoading}
            message={t("Sending verification code to e-mail...")}
            duration={10000}
          />
          <IonAlert
            isOpen={showError}
            onDidDismiss={() => setShowError(false)}
            header={t("Error")}
            message={t("Could not send verification code. If the problem persists, please contact flavoria@utu.fi")}
            buttons={[t("OK")]}
          />
        </div>
      </IonContent>
    </>
  );
}

export default LoginPage;
